import React, { useCallback, useMemo } from 'react';
import { first } from 'lodash';

import StripeAccountVerifierModal from 'components/modal/StripeAccountVerifierModal.component';
import DELAY from 'constants/delay.const';
import useStateWhileMounted from 'hooks/useStateWhileMounted.hook';
import useTip from 'hooks/useTip.hook';
import { useTranslation } from 'providers/GILT.provider';
import { withSalonRestriction } from 'providers/SalonBasicInfo.provider';
import getAnyOrVoid from 'utils/accessors/getAnyOrVoid.util';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import delayed from 'utils/functions/delayed.util';
import alwaysNoop from 'utils/functions/nullary/alwaysNoop.util';
import logger from 'utils/logger.util';
import asObject from 'utils/objects/asObject.util';
import isFullArray from 'utils/predicates/array/isFullArray.util';
import { isTimeBefore, isTimeEqual, isTimeNotEqual } from 'utils/predicates/time/compare.util';

import ActivityFeedIcon from 'activity/components/ActivityFeedIcon';
import Footer from 'activity/components/ItemFooter';
import PopupModal from 'common/PopupModal';
import SmsButtonNew from 'scheduler/components/common/SmsButtonNew';

const L = logger('BookingItem');

const USE_STATUS = true;
const USE_EXTRA_FETCH = true;

const fromProceduresToStart = (procedures) => {
  if (!isFullArray(procedures)) return '';

  const comparator = (a, b) => {
    const stA = asObject(a).start_time;
    const stB = asObject(b).start_time;

    // eslint-disable-next-line no-nested-ternary
    return isTimeEqual(stA, stB) ? 0 : isTimeBefore(stA, stB) ? -1 : 1;
  };

  const copy = [...procedures].sort(comparator);

  return asObject(first(copy)).start_time;
};

const BookingItem = ({
  bookingID: bookingId,
  booking_start_time: bookingStartTime,
  clientID,
  clientName,
  date,
  feedData,
  getAppointment,
  goToSchedulerAndOpenModal,
  messageSent: isMessageSent,
  notes,
  onReadToggle,
  salonId,
  seen: isSeen,
  serviceName,
  stylistName,
  time,
  timestamp,
  isRestrictedToCriticalServices,
  isRestrictionCheckEnabled,
  enableRestrictionCheck,
  disableRestrictionCheck,
  hideViewAppointment,
}) => {
  const { val: isDeleted, yay: markDeleted } = useTip(false);
  const { val: isMoved, yay: markMoved } = useTip(false);
  const { val: isModalRendered, yay: showModal, nay: hideModal } = useTip(false);
  const { val: isSmsRendered, yay: showSms, nay: hideSms, set: setSmsRendered } = useTip(false);

  const [newStart, setNewStart] = useStateWhileMounted();
  const { t } = useTranslation();

  // eslint-disable-next-line no-nested-ternary
  const popupTitle = isDeleted ? t('composite.activityFeed.bookingItem.notFound') : isMoved ? t('composite.activityFeed.bookingItem.moved') : '';

  // eslint-disable-next-line no-nested-ternary
  const popupMessage = isMoved ? t('composite.activityFeed.bookingItem.newTime') : isDeleted ? t('composite.activityFeed.bookingItem.deleted') : '';

  const handleCloseModal = useCallback(() => {
    hideModal();
    if (isMoved) goToSchedulerAndOpenModal(bookingId, newStart);
  }, [isMoved, bookingId, goToSchedulerAndOpenModal, hideModal, newStart]);

  const handleSeen = useCallback(() => void onReadToggle(!isSeen), [isSeen, onReadToggle]);

  const handleTextClient = useCallback(() => {
    enableRestrictionCheck();
    if (isRestrictedToCriticalServices) return;

    showSms();
  }, [enableRestrictionCheck, isRestrictedToCriticalServices, showSms]);

  const handleView = useCallback(() => {
    const filteredItems = feedData.filter((item) => item.target_id === bookingId);
    const { booking_start_time: oldStart } = asObject(first(filteredItems));

    L.debug('handleView()', bookingId, { oldStart }, { filteredItems, feedData });

    if (!USE_EXTRA_FETCH) {
      return void goToSchedulerAndOpenModal(bookingId, bookingStartTime);
    }

    return void L.unignore('handleView()', 'getAppointment:', getAppointment(salonId, bookingId)).then((result) => {
      const status = getAnyOrVoid('json.status', result);
      const foundStart = fromProceduresToStart(getAnyOrVoid('json.procedures', result));

      L.debug('handleView()', 'getAppointment().then()', { salonId, bookingId, status, foundStart, oldStart });

      if (USE_STATUS && !status) {
        markDeleted();
        showModal();
        return;
      }

      if (foundStart && isTimeNotEqual(foundStart, oldStart)) {
        markMoved();
        setNewStart(foundStart);
        showModal();
        return;
      }

      goToSchedulerAndOpenModal(bookingId, bookingStartTime);
    });
  }, [bookingId, bookingStartTime, feedData, getAppointment, goToSchedulerAndOpenModal, markDeleted, markMoved, salonId, setNewStart, showModal]);

  useMemo(() => {
    if (!isMessageSent) return;
    L.debug('useEffect()', 'messageSent:', isMessageSent);
    delayed(DELAY.autohide1, hideSms);
  }, [hideSms, isMessageSent]);

  return (
    <div data-bem="BookingItem" className="comment-content 1">
      <div data-bem="BookingItem__feed-text" className="feed-text" role="button" tabIndex={bookingId} onClick={alwaysNoop} style={{ cursor: 'pointer' }}>
        <div className={isSeen ? 'new-item-indicator' : 'new-item-indicator available'} />

        <div className="client-name">
          <ActivityFeedIcon iconName="ico-online" />
          {t('composite.activityFeed.bookingItem.clientName', { clientName })}

          <span>&nbsp;{serviceName}</span>
        </div>

        <span className="additional-info">{t('composite.activityFeed.bookingItem.additionalInfo', { stylistName, date, time })}</span>
        <div className="client-notes">{fromAnyToBool(notes) && <div>{t('composite.activityFeed.bookingItem.notes', { notes })}</div>}</div>
      </div>

      {!hideViewAppointment && (
        <div data-bem="BookingItem__view-appointment" className="view-appointment" role="button" tabIndex={bookingId} onClick={handleView}>
          {t('composite.activityFeed.bookingItem.viewAppointmentButton')}
        </div>
      )}

      <div data-bem="BookingItem__text-client" className="relative text1">
        <div className="text-client" role="button" tabIndex={bookingId} onClick={handleTextClient}>
          {t('composite.activityFeed.bookingItem.textClientButton')}
        </div>
      </div>

      {isModalRendered && (
        <PopupModal
          tag="BookingItem__deleted-or-moved"
          popupTitle={popupTitle}
          popupMessage={popupMessage}
          buttonText={t('composite.activityFeed.bookingItem.okay')}
          buttonCallback={handleCloseModal}
        />
      )}

      <Footer seen={isSeen} markAsRead={handleSeen} timestamp={timestamp} />

      {isSmsRendered && (
        <SmsButtonNew
          clientId={[clientID]}
          bucketType="appointment"
          bookingID={bookingId}
          clientName={clientName}
          isOpen={isSmsRendered}
          smsComposeModalOpen={isSmsRendered}
          showParentSmsPopup={setSmsRendered}
        />
      )}

      <StripeAccountVerifierModal tag="BookingItem__restriction-modal" enabled={isRestrictionCheckEnabled} onCancel={disableRestrictionCheck} onClose={disableRestrictionCheck} />
    </div>
  );
};

const BookingItemHoc = withSalonRestriction(BookingItem);

export default BookingItemHoc;
